import { CommandContext } from "../../services/doc/internal/yDocHighLevelAPI";
import { docsCRDTService } from "../../services/crdt/docsCRDTService";
import { createFileLogger } from '../../../../utils/logger';
import { Commands as YjsCommands } from "../../services/doc/internal/yDocHighLevelAPI";

// Re-export YjsCommands for use in other command files
export { YjsCommands };

const log = createFileLogger('DOCS_COMMANDS');

interface CommandVersion {
  id: string;
  name: string;
  description: string;
  experimental?: boolean;
}

export const COMMAND_VERSIONS: CommandVersion[] = [
  {
    id: 'v1',
    name: 'Standard Commands',
    description: 'Stable, proven command set with node-level operations'
  },
  {
    id: 'hybrid',
    name: 'Hybrid Commands', 
    description: 'Semantic selectors with improved AI usability'
  }
];

let enabledVersions = new Set(['v1']);

export function getEnabledVersions(): Set<string> {
  return new Set(enabledVersions);
}

export function setEnabledVersions(versions: string[]): void {
  enabledVersions = new Set(versions);
  log.log('[CommandVersions] Updated enabled versions:', Array.from(enabledVersions));
}

export function isVersionEnabled(version: string): boolean {
  return enabledVersions.has(version);
}

export function isYjsCommandsEnabled(): boolean {
  return true;
}

export const processHtmlFormatting = (text: string): { 
  text: string; 
  formatting?: Array<{
    start: number;
    end: number;
    marks: Record<string, any>;
  }>;
} => {
  if (!text || typeof text !== 'string') {
    return { text };
  }

  if (text.includes('<') && text.includes('>')) {
    log.log('[AI_TOOL_LOG] [processHtmlFormatting] Processing text with HTML-like tags:', {
      originalText: text.substring(0, 200) + (text.length > 200 ? '...' : ''),
      textLength: text.length,
      containsStrong: text.includes('<strong'),
      containsEm: text.includes('<em'),
      containsB: text.includes('<b'),
      containsI: text.includes('<i'),
      containsU: text.includes('<u'),
      containsFontSize: text.includes('<fontSize'),
      containsColor: text.includes('<color')
    });
  }

  const formatting: Array<{
    start: number;
    end: number;
    marks: Record<string, any>;
  }> = [];

  const allMatches: Array<{
    start: number;
    end: number;
    content: string;
    marks: Record<string, any>;
  }> = [];

  const fontSizeRegex = /<fontSize\s+size="(\d+)">(.*?)<\/fontSize>/g;
  let fontSizeMatch;
  while ((fontSizeMatch = fontSizeRegex.exec(text)) !== null) {
    const size = fontSizeMatch[1];
    allMatches.push({
      start: fontSizeMatch.index,
      end: fontSizeMatch.index + fontSizeMatch[0].length,
      content: fontSizeMatch[0],
      marks: { fontSize: parseInt(size) }
    });
  }

  const strongRegex = /<strong[^>]*>(.*?)<\/strong>/g;
  let strongMatch;
  while ((strongMatch = strongRegex.exec(text)) !== null) {
    allMatches.push({
      start: strongMatch.index,
      end: strongMatch.index + strongMatch[0].length,
      content: strongMatch[0],
      marks: { strong: true }
    });
  }

  const emRegex = /<em[^>]*>(.*?)<\/em>/g;
  let emMatch;
  while ((emMatch = emRegex.exec(text)) !== null) {
    allMatches.push({
      start: emMatch.index,
      end: emMatch.index + emMatch[0].length,
      content: emMatch[0],
      marks: { em: true }
    });
  }

  const bRegex = /<b[^>]*>(.*?)<\/b>/g;
  let bMatch;
  while ((bMatch = bRegex.exec(text)) !== null) {
    allMatches.push({
      start: bMatch.index,
      end: bMatch.index + bMatch[0].length,
      content: bMatch[0],
      marks: { strong: true }
    });
  }

  const iRegex = /<i[^>]*>(.*?)<\/i>/g;
  let iMatch;
  while ((iMatch = iRegex.exec(text)) !== null) {
    allMatches.push({
      start: iMatch.index,
      end: iMatch.index + iMatch[0].length,
      content: iMatch[0],
      marks: { em: true }
    });
  }

  const uRegex = /<u[^>]*>(.*?)<\/u>/g;
  let uMatch;
  while ((uMatch = uRegex.exec(text)) !== null) {
    allMatches.push({
      start: uMatch.index,
      end: uMatch.index + uMatch[0].length,
      content: uMatch[0],
      marks: { underline: true }
    });
  }

  const colorRegex = /<color\s+value="([^"]+)">(.*?)<\/color>/g;
  let colorMatch;
  while ((colorMatch = colorRegex.exec(text)) !== null) {
    const color = colorMatch[1];
    allMatches.push({
      start: colorMatch.index,
      end: colorMatch.index + colorMatch[0].length,
      content: colorMatch[0],
      marks: { color }
    });
  }

  allMatches.sort((a, b) => a.start - b.start);

  let currentOffset = 0;
  let processedText = text;

  for (const match of allMatches) {
    const innerContent = match.content.replace(/<[^>]*>/g, '');
    const newStart = match.start - currentOffset;
    processedText = processedText.replace(match.content, innerContent);
    const newEnd = newStart + innerContent.length;
    formatting.push({
      start: newStart,
      end: newEnd,
      marks: match.marks
    });
    currentOffset += match.content.length - innerContent.length;
  }

  processedText = processedText.replace(/<comment--[^>]*>/g, '');
  processedText = processedText.replace(/<\/comment--[^>]*>/g, '');
  processedText = processedText.replace(/<comment[^>]*>/g, '');
  processedText = processedText.replace(/<\/comment[^>]*>/g, '');
  processedText = processedText.replace(/<[^>]*>/g, '');
  processedText = processedText.replace(/\s+/g, ' ').trim();
  
  if (processedText !== text) {
    log.log('[AI_TOOL_LOG] [processHtmlFormatting] Processed text:', {
      originalLength: text.length,
      processedLength: processedText.length,
      processedText: processedText.substring(0, 100) + (processedText.length > 100 ? '...' : ''),
      formattingCount: formatting.length,
      formatting: formatting.slice(0, 3),
      allMatchesCount: allMatches.length,
      hasStrongTags: text.includes('<strong'),
      hasEmTags: text.includes('<em'),
      hasBTags: text.includes('<b'),
      hasITags: text.includes('<i')
    });
  }
  
  return { 
    text: processedText, 
    formatting: formatting.length > 0 ? formatting : undefined 
  };
};

// Removed createUnifiedContext - use direct store access instead

// Removed createUnifiedContextForDocument - use direct store access instead

// Removed cleanupTemporaryContext - no longer needed without unifiedContext

// Removed createUnifiedContextWithRetry - use direct store access instead

export const saveToDatabase = (context: CommandContext): void => {
    try {
      docsCRDTService.saveToDB(context.docId, context.yxml);
      log.log('[AI_TOOL_LOG] [StructuredCommands] Successfully saved to database:', context.docId);
    } catch (error) {
      log.error('[AI_TOOL_LOG] [StructuredCommands] Error saving to database:', error);
    }
};

export const readDocumentAsMarkdown = (context: CommandContext, operation: string, params: any): any => {
    try {
      log.log('[AI_TOOL_LOG] [readDocumentAsMarkdown] Converting YJS to Markdown:', {
        operation,
        docId: context.docId,
        yxmlLength: context.yxml.length
      });
  
      let markdown = '';
      let plainText = '';
      let nodes: any[] = [];
  
      try {
        const { YJSMarkdownBridge } = require('../../../../shared/utils/yjsMarkdownBridge');
        
        log.log('[AI_TOOL_LOG] [readDocumentAsMarkdown] 🔍 About to call YJSMarkdownBridge.toMarkdown', {
          docId: context.docId,
          yxmlLength: context.yxml.length,
          hasYDoc: !!context.doc
        });
        
        markdown = YJSMarkdownBridge.toMarkdown(context.doc, {
          preserveAttributes: false,
          handleSuggestions: 'include', // ✅ CRITICAL FIX: Claude sees suggestions as accepted content
          maxDepth: 50
        });
        
        log.log('[AI_TOOL_LOG] [readDocumentAsMarkdown] 🔍 YJSMarkdownBridge.toMarkdown result:', {
          markdownLength: markdown.length,
          markdownPreview: markdown.substring(0, 200),
          containsFontFamily: markdown.includes('<fontFamily') || markdown.includes('fontFamily='),
          containsFontFamilyCount: (markdown.match(/<fontFamily/g) || []).length
        });
        
        plainText = YJSMarkdownBridge.extractPlainText(context.doc, {
          maxLength: 8000,
          preserveLineBreaks: true,
          stripFormatting: true
        });
        
        log.log('[AI_TOOL_LOG] [readDocumentAsMarkdown] ✅ Used YJSMarkdownBridge successfully', {
          markdownLength: markdown.length,
          plainTextLength: plainText.length,
          CRITICAL_CHECK_fontFamilyInOutput: markdown.includes('<fontFamily')
        });
        
      } catch (bridgeError) {
        log.error('[AI_TOOL_LOG] [readDocumentAsMarkdown] YJSMarkdownBridge FAILED - this is a critical error!', bridgeError);
        
        // 🚨 CRITICAL: DO NOT USE FALLBACK - fallbacks are the root cause of raw markdown in editor
        // Even "safe" text extraction can return malformed content that contaminates the AI pipeline
        // This was causing raw markdown and XML tags to appear in the ProseMirror editor
        
        throw new Error(`Failed to convert YJS document to markdown for AI processing: ${bridgeError instanceof Error ? bridgeError.message : 'Unknown error'}. YJSMarkdownBridge is required for proper document conversion.`);
        
        // ❌ REMOVED: All fallback text extraction logic including "safe" extraction
        // Any form of toString() or manual text extraction can return XML-like content
        // that contaminates the AI pipeline and causes raw markdown to appear in the editor
      }
  
      if (operation === 'all' || operation === 'chunk') {
        const readResult = YjsCommands.read.all(context);
        nodes = readResult.nodes || [];
        log.log('[AI_TOOL_LOG] [readDocumentAsMarkdown] YjsCommands.read.all result:', {
          nodesCount: nodes.length,
          hasNodes: nodes.length > 0,
          firstThreeNodeIds: nodes.slice(0, 3).map(n => ({ id: n.id, type: n.type, textPreview: (n.text || '').substring(0, 50) })),
          totalNodesInResult: readResult.metadata?.totalNodes || 'unknown'
        });
      } else if (operation === 'byId') {
        nodes = YjsCommands.read.byId(context, params.nodeId).nodes || [];
      } else if (operation === 'range') {
        nodes = YjsCommands.read.range(context, params.fromId, params.toId).nodes || [];
      } else if (operation === 'search') {
        nodes = YjsCommands.read.search(context, params.query, params).nodes || [];
      }
  
      log.log('[AI_TOOL_LOG] [readDocumentAsMarkdown] Conversion complete:', {
        markdownLength: markdown.length,
        plainTextLength: plainText.length,
        nodeCount: nodes.length,
        hasValidNodeIds: nodes.length > 0 && nodes.every(n => n.id),
        sampleNodeIds: nodes.slice(0, 5).map(n => n.id).filter(Boolean)
      });
  
      return {
        markdown: markdown,
        text: plainText,
        nodes: nodes.map((node, index) => {
          const llmNodeId = node.id;
          
          const mappedNode = {
            id: llmNodeId,
            originalId: node.id,
            type: node.type,
            text: node.text,
            attributes: node.attrs
          };
          
          if (index < 3) {
            log.log(`[AI_TOOL_LOG] [readDocumentAsMarkdown] Mapped node ${index}:`, {
              originalId: node.id,
              mappedId: mappedNode.id,
              type: mappedNode.type,
              textPreview: (mappedNode.text || '').substring(0, 50) + '...',
              hasValidId: !!mappedNode.id && mappedNode.id !== 'undefined'
            });
          }
          
          return mappedNode;
        }),
        metadata: {
          operation,
          contentFormat: 'markdown',
          preservesFormatting: true,
          eliminatesSerializationBugs: true
        }
      };
  
    } catch (error) {
      log.error('[AI_TOOL_LOG] [readDocumentAsMarkdown] Critical error in YJS → Markdown conversion:', error);
      
      // 🚨 SALVAGE STRATEGY: Flag the issue but don't tank the entire AI action
      console.error('[readDocumentAsMarkdown] 🚨 YJSMarkdownBridge failed - using emergency fallback!');
      console.error('[readDocumentAsMarkdown] 🚨 INVESTIGATE: YJSMarkdownBridge failure needs attention!');
      
      // Try to salvage with legacy commands (with clear warnings)
      try {
        console.warn('[readDocumentAsMarkdown] ⚠️ Using legacy YjsCommands as emergency fallback');
        const legacyResult = YjsCommands.read.all(context);
        
        if (legacyResult && legacyResult.nodes) {
          // Create basic markdown from legacy nodes
          const salvageMarkdown = legacyResult.nodes.map((node: any) => {
            const text = (node.text || '').trim();
            return text ? `<!-- ${node.id} -->\n${text}` : `<!-- ${node.id} -->\n[Empty ${node.type}]`;
          }).join('\n\n');
          
          console.warn('[readDocumentAsMarkdown] ⚠️ SALVAGED: Used legacy fallback - may have formatting issues');
          
          return {
            markdown: salvageMarkdown,
            plainText: legacyResult.nodes.map((n: any) => n.text || '').join('\n'),
            nodes: legacyResult.nodes,
            salvaged: true,
            warning: 'Content extracted using legacy fallback due to YJSMarkdownBridge failure'
          };
        }
      } catch (legacyError) {
        console.error('[readDocumentAsMarkdown] ❌ Legacy fallback also failed:', legacyError);
      }
      
      // Last resort: return minimal structure to keep AI action alive
      console.error('[readDocumentAsMarkdown] 🚨 ALL EXTRACTION METHODS FAILED - returning minimal content');
      return {
        markdown: '# Document Content Unavailable\n\nDocument content could not be extracted due to technical issues.',
        plainText: 'Document content unavailable',
        nodes: [],
        salvaged: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
};

// ✅ REMOVED: getDocumentContentByNodeIds - replaced with direct Entity-First pattern in commands


export const convertYJSToMarkdown = (ydoc: any, options: any): string => {
    try {
      const { YJSMarkdownBridge } = require('../../../../shared/utils/yjsMarkdownBridge');
      
      const markdown = YJSMarkdownBridge.toMarkdown(ydoc, {
        preserveAttributes: options.preserveAttributes || false,
        handleSuggestions: options.handleSuggestions || 'include', // ✅ CRITICAL FIX: Default to include for Claude
        maxDepth: options.maxDepth || 50
      });
      
      log.log('[AI_TOOL_LOG] [convertYJSToMarkdown] ✅ Used YJSMarkdownBridge successfully', {
        markdownLength: markdown.length,
        hasStructure: markdown.includes('#') || markdown.includes('*')
      });
      
      return markdown;
    } catch (bridgeError) {
      log.error('[AI_TOOL_LOG] [convertYJSToMarkdown] YJSMarkdownBridge FAILED - this is a critical error that must be fixed, not worked around!', bridgeError);
      
      throw new Error(`YJSMarkdownBridge conversion failed: ${bridgeError instanceof Error ? bridgeError.message : 'Unknown error'}. This conversion is required for proper AI integration.`);
    }
};

export const extractPlainTextFromYJS = (ydoc: any, maxLength?: number): string => {
    try {
      const { YJSMarkdownBridge } = require('../../../../shared/utils/yjsMarkdownBridge');
      
      const plainText = YJSMarkdownBridge.extractPlainText(ydoc, {
        maxLength: maxLength || 8000,
        preserveLineBreaks: true,
        stripFormatting: true
      });
      
      log.log('[AI_TOOL_LOG] [extractPlainTextFromYJS] ✅ Used YJSMarkdownBridge for plain text', {
        textLength: plainText.length,
        truncated: maxLength ? plainText.length >= maxLength : false
      });
      
      return plainText;
    } catch (bridgeError) {
      log.error('[AI_TOOL_LOG] [extractPlainTextFromYJS] YJSMarkdownBridge FAILED - this is critical!', bridgeError);
      
      throw new Error(`Failed to extract plain text from YJS document: ${bridgeError instanceof Error ? bridgeError.message : 'Unknown error'}. YJSMarkdownBridge is required for safe text extraction.`);
    }
};

export const extractDocumentOutline = (context: CommandContext, maxDepth: number) => {
    const nodes = YjsCommands.read.all(context).nodes || [];
    
    const outline = {
      headings: [] as Array<{level: number, text: string, nodeId: string, preview: string}>,
      firstParagraph: '',
      structure: {} as Record<string, number>
    };

    let firstParagraphFound = false;

    for (const node of nodes) {
        if (node.type === 'heading' && node.attrs && node.attrs.level <= maxDepth) {
            outline.headings.push({
                level: node.attrs.level,
                text: node.text,
                nodeId: node.id,
                preview: ''
            });
            outline.structure[`h${node.attrs.level}`] = (outline.structure[`h${node.attrs.level}`] || 0) + 1;
        } else if (node.type === 'paragraph' && node.text && !firstParagraphFound) {
            outline.firstParagraph = node.text.substring(0, 300) + (node.text.length > 300 ? '...' : '');
            firstParagraphFound = true;
        }
    }
    
    return outline;
};

export const getDocumentWordCount = (context: CommandContext): number => {
    const content = extractPlainTextFromYJS(context.doc) || '';
    return content.split(/\s+/).filter(word => word.length > 0).length;
};

export const getYJSDocumentStats = (ydoc: any): any => {
    try {
      const yxml = ydoc.getXmlFragment('prosemirror');
      const plainText = extractPlainTextFromYJS(ydoc);
      
      return {
        totalElements: yxml.length,
        wordCount: plainText.split(/\s+/).filter(word => word.length > 0).length,
        characterCount: plainText.length,
        hasStructure: yxml.length > 1
      };
    } catch (error) {
      return {
        totalElements: 0,
        wordCount: 0,
        characterCount: 0,
        hasStructure: false
      };
    }
};

export const SHARED_PARAMS = {
    windowId: {
      name: 'windowId',
      type: 'string',
      description: 'The LLMId of the window containing the document (e.g., "window_abc_def")',
      required: true
    },
    spaceId: {
      name: 'spaceId',
      type: 'string',
      description: 'The ID of the space containing the document',
      required: true
    },
    description: {
      name: 'description',
      type: 'string',
      description: 'Description of the operation for change tracking',
      required: false
    }
} as const;
